import { Component, Inject, OnInit, Input, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { FormAlertType } from 'src/app/core/constants/constants';
import { Tenant, TenantClientAction, TenantIntegrationStatusType, TenantOauthStatusType, TenantType, ZoomTenant } from 'src/app/core/interfaces/tenants';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { Util } from 'src/app/core/utils/Util';
import { ExternalEventsHandlerService } from '../../../../../core/services/external-events-handler.service';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { FeatureToggleService } from 'src/app/core/services/feature-toggle.service';
import { FeatureFlags } from 'src/app/core/interfaces/feature-flags';

const logContext = 'ZappaPhoneConfigDialogComponent';

@Component({
  selector: 'app-zappa-phone-config-dialog',
  templateUrl: './zappa-phone-config-dialog.component.html',
  styleUrls: ['./zappa-phone-config-dialog.component.scss']
})
export class ZappaPhoneConfigDialogComponent implements OnInit, OnDestroy {


	configForm = this.formBuilder.group({
		clientId: ['', []],
		clientSecret: ['', []],
		notificationSecretToken: ['', []]
	});
	private subscription: Subscription;
	oAuthUrl: string;
	oAuthWindow = null;
	previousConnectionData: any = {};
	existingTenant: Tenant;
	inDevelopMode: boolean = false; // value set by a feature flag ALLOW_DEV_ZAPPA_PHONE
	isEdit: boolean = false;

	isDeactivated: any;
	consentReceived: boolean = false;
	intervalId: number;
	authWasSuccessful: boolean = false;
	changedTheTenant: boolean = false;

	showFormError: boolean = false;
	alertType: string = FormAlertType.SUCCESS;
	errMsg: string = "";
	errMsgStyle: any = { 'max-width': '600px' };

	constructor( @Inject(MAT_DIALOG_DATA) public data: any,
		public dialogRef: MatDialogRef<ZappaPhoneConfigDialogComponent>,
		private translateService: TranslateService,
		private domSanitizer: DomSanitizer,
		private matIconRegistry: MatIconRegistry,
		private formBuilder: FormBuilder,
		private extEventHandlerService: ExternalEventsHandlerService,
		private spinnerService: SpinnerService,
		private featureToggleService: FeatureToggleService ) 
	{
		this.matIconRegistry.addSvgIcon(
			'trash_can',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/trash.svg")
		);
	}

	async ngOnInit() 
	{
		this.spinnerService.setSpinnerMessageByKey('app.loading');
		this.spinnerService.showSpinner();

		this.inDevelopMode = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_DEV_ZAPPA_PHONE);

		this.subscription = this.extEventHandlerService.ZappaPhoneConsentReceived.subscribe( event => {
			console.debug(`${logContext}.getAccountMonitorDetails Received MONITOR CONSENT event`, event);
			this.consentReceived = true;
			this.handleConsentMessage(event);
		});

		return this.initForm().then( () => {
			this.spinnerService.hideSpinner();
		})
		.catch( err => {
			this.displayErrorBasedOnException( err, false );
			this.spinnerService.hideSpinner();
		});
	}

	initForm(): Promise<void>
	{
		let product = this.data.product;

		return product.service.getTenantData(this.data.account).then( tenant => {
			if ( tenant && tenant.tenantId )
			{
				this.isEdit = true;
				this.existingTenant = tenant;
				if (this.inDevelopMode)
				{
					this.configForm.patchValue({
						clientId: tenant?.zoomTenant?.clientId,
						clientSecret: tenant?.zoomTenant?.clientSecret,
						notificationSecretToken: tenant?.zoomTenant?.notificationSecretToken
					});
				}

				// not authorized
				if (tenant.status?.oauth?.state != TenantOauthStatusType.AUTHORIZED)
				{
					this.configForm.markAsDirty();
					this.alertType = FormAlertType.ERROR;
					this.errMsg = tenant.status?.oauth?.details ? this.extractErrorAndTranslate(tenant.status.oauth.details) :
						this.translateService.instant('integrations.zappa.config-dialog.errors.' + tenant.status.oauth?.state);
					this.showFormError = true;
					this.spinnerService.hideSpinner();
				}
				// not connected
				else if (tenant.status.integration?.state != TenantIntegrationStatusType.CONNECTED) 
				{
					this.configForm.markAsDirty();
					this.alertType = FormAlertType.ERROR;
					this.errMsg = tenant.status.integration?.details ? this.extractErrorAndTranslate(tenant.status.integration.details) : 
						this.translateService.instant('integrations.zappa.config-dialog.errors.not-connected');
					this.showFormError = true;
					this.spinnerService.hideSpinner();
				}
				else 
				{
					// if we get here we will assume all is OK with communication with zoom
					// but we are still allowing them to authorize. 
					this.configForm.markAsDirty();
					this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.integration-working');
					this.showFormError = true;
					this.alertType = FormAlertType.SUCCESS;
				}
			}
		})
	}

	onRemoveIntegration() {
		console.log("Remove the integration - when successful, close the dialog");
		let result = {
			remove: true
		}
		this.dialogRef.close( result );
	}

	async onAuthorize(event) {
		console.log("Save the changes to the integration - and authorize");
		this.showFormError = false;
		let product = this.data.product;

		let tenant: Tenant = {
			tenantId: this.existingTenant?.tenantId,
			tenantType: TenantType.ZOOM,
			clientRedirectUrl: location.origin,
		}

		if (this.inDevelopMode)
		{
			let data = this.configForm.getRawValue();

			let zoomTenant: ZoomTenant = {
				clientId: data.clientId?.trim(),
				clientSecret: data.clientSecret?.trim(),
				notificationSecretToken: data.notificationSecretToken?.trim()
			};
			tenant.zoomTenant = zoomTenant;
		}

		let needToSave: boolean = false;

		// if this is a create or any of the fields change, a save is required
		if (!this.existingTenant || ( this.inDevelopMode && 
			(this.existingTenant?.zoomTenant?.clientId != tenant.zoomTenant?.clientId || 
			this.existingTenant?.zoomTenant?.clientSecret != tenant.zoomTenant?.clientSecret || 
			this.existingTenant?.zoomTenant?.notificationSecretToken != tenant.zoomTenant?.notificationSecretToken))) {
			needToSave = true;
			// in this case the micro determines if there is a need to auth
		}

		if (needToSave) {
			this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
			this.spinnerService.showSpinner();
			let zoomDelta = _.cloneDeep(tenant.zoomTenant);

			if (this.existingTenant)
			{
				// we want only to send the delta
				zoomDelta.clientId = tenant.zoomTenant?.clientId != this.existingTenant.zoomTenant?.clientId ? tenant.zoomTenant?.clientId : undefined;
				zoomDelta.clientSecret = tenant.zoomTenant?.clientSecret != this.existingTenant.zoomTenant?.clientSecret ? tenant.zoomTenant?.clientSecret : undefined;
				zoomDelta.notificationSecretToken = tenant.zoomTenant?.notificationSecretToken != this.existingTenant.zoomTenant?.notificationSecretToken ? tenant.zoomTenant?.notificationSecretToken : undefined;
				tenant.zoomTenant = zoomDelta;
			}
			try {
				let savedTenant = await product.service.saveTenantData(this.data.account, tenant);
				this.changedTheTenant = true;
				this.existingTenant = savedTenant;

				if (!savedTenant?.redirectUrl)
				{
					// unfortunately the micro believes it is doesn't need to 
					// return the redirect URL in some scenarios even though 
					// there is a requirement in the UI to ALWAYS allow reconnect
					// so in this case we must get the tenant again
					this.existingTenant = await product.service.getTenantData(this.data.account);
				}
				this.spinnerService.hideSpinner();
			}
			catch (err)
			{
				this.displayErrorBasedOnException( err, true );
				this.spinnerService.hideSpinner();
				return; // there was an error, we can no longer continue
			}
		}
			
		// Current conditions when the auth process must be started
		// - there needs to be a redirect url in the tenant
		// - the micro determines it via the 202 statusCode it returns
		// - the integration connection/ auth is in error
		// This means the user could always re-connect if they wanted as long as there is a redirect url
		// This also means that even if the user only changes the notify token (which only requires a save),  
		// auth will automatically start if the integration or oauth state is in error. I know, a bit confusing.
		if (this.existingTenant && this.existingTenant?.redirectUrl != undefined && 
			(this.existingTenant?.action == TenantClientAction.MUST_AUTH || 
			this.existingTenant.status?.integration?.state != TenantIntegrationStatusType.CONNECTED || 
			this.existingTenant.status.oauth?.state != TenantOauthStatusType.AUTHORIZED ||
			needToSave == false))
		{
			this.changedTheTenant = true;
			this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.authorize-inprogress');
			this.showFormError = true;
			this.alertType = FormAlertType.WARNING;
			this.oAuthUrl = this.existingTenant.redirectUrl;
			if(this.oAuthWindow) {
				this.oAuthWindow.close();
				this.oAuthWindow = null;
			}
			console.debug(`${logContext}.onAuthorize open a window with URL=`, this.oAuthUrl);
			this.oAuthWindow = this.createOauthWindow(600, 600, 200, 200);
			this.oAuthWindow.location.href = this.oAuthUrl;
			this.consentReceived = false;
			this.intervalId = window.setInterval(this.checkOAuthWindow.bind(this), 250);
		}
		else if (needToSave == true) // scenario: Auth/ integration connection was not in error and the user only saved new notify token
		{
			this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.save-only-success');
			this.showFormError = true;
			this.alertType = FormAlertType.SUCCESS;
		}
	}

	onClose(event) {
		this.data.product.service.clearStatusMessageCache(this.data.account);
		let result = {
			setupComplete: this.changedTheTenant ? true : undefined // notifies the caller that something changed to change the state of the integration
		}
		this.dialogRef.close(result);
	}

	private createOauthWindow(width = 600, height = 1000, left = 0, top = 0): Window {
        let newWindow = window.open('about:blank', '_blank', `width=${width},height=${height},left=${left},top=${top}`);
        newWindow.addEventListener('message', event => {
            console.debug(`${logContext}.createOauthWindow message: `, event);
        });
        return newWindow;
    }

	private checkOAuthWindow(): void {
        
        if(this.isDeactivated && this.oAuthWindow){ 
            this.oAuthWindow.close();
        }

        if(!this.oAuthWindow || this.oAuthWindow.closed) {
            window.clearInterval(this.intervalId);
			this.oAuthWindow = null;
			if ( this.consentReceived == false )
				this.handleConsentMessage({});
			else
				this.consentReceived = false;
            // let redirect = this.getOAuth2RedirectUrl();
            // let error = this.translateService.instant('messages.errorUnknown');
            // redirect =`${redirect}?connected=false&integrationid=${this.integration.integrationId}&connectionid=${this.connection.connectionId}&error=${error}`;
            // window.location.href = redirect;
            return;
        }
    }

	handleConsentMessage(event)
	{
		console.log(`${logContext}.handleConsentMessage, event received`, event);
		if ( event?.connected == true )
		{
			this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.auth-success');
			this.showFormError = true;
			this.alertType = FormAlertType.SUCCESS;
			this.consentReceived = true;
			this.authWasSuccessful = true;
		}
		else if ( event?.error != undefined )
		{
			this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.consent-error') +
				": " + this.extractErrorAndTranslate(event.error);
			this.showFormError = true;
			this.alertType = FormAlertType.ERROR;
			this.consentReceived = true;
			this.authWasSuccessful = false;
		}
		else // the user closed the window before auth was performed
		{
			this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.user-closed-error');
			this.showFormError = true;
			this.alertType = FormAlertType.ERROR;
			this.consentReceived = false;
			this.authWasSuccessful = false;
		}
	}

	// is this the proper error handling?
	displayErrorBasedOnException( err: any, onSave: boolean ) {
		let theMessage = '';
		console.error("Display this error to the user", err);
		if ( onSave == true ) 
			this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.generic-save');
		else
			this.errMsg = this.translateService.instant('integrations.zappa.config-dialog.errors.generic-retrieve');

		theMessage = Util.extractMessageFromError(err);

		// concatenate the generic and specific message
		if ( theMessage != undefined || theMessage?.length > 0 )
		{
			theMessage = this.extractErrorAndTranslate(theMessage);
			this.errMsg = this.errMsg + ": " + theMessage;
		}

		this.alertType = FormAlertType.ERROR;
		this.showFormError = true;
	}

	extractErrorAndTranslate(message: string): string
	{
		// here we will try figure out if there is a known issue
		// expect this to grow as we understand the errors
		if (message.indexOf('Invalid client_id or client_secret') != -1)
		{
			message = this.translateService.instant('integrations.zappa.config-dialog.errors.invalid-client');
		}
		else if (message.indexOf('4711') != -1)
		{
			message = this.translateService.instant('integrations.zappa.config-dialog.errors.4711');
		}
		return message;
	}

	isConnectButtonDisabled(): boolean
	{
		if (this.inDevelopMode == true)
			return !(this.configForm?.valid) || this.oAuthWindow != null || !(this.configForm?.dirty);
		else
			return (this.oAuthWindow != null);
	}

	ngOnDestroy() {
		if (this.subscription) {
		  this.subscription.unsubscribe();
		}
	}
}
